import * as React from 'react';
import { Notification } from 'components/Notifications/types';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material';
import Flex from 'ui/Flex/Flex';
import { useMarkSingleNotificationRead } from 'components/Notifications/hooks';
import AvatarComponent from './Avatar';
import { getCreationDate } from './helpers';

const StyContainer = styled('div', {
    name: 'StyContainer',
})(() => ({
    lineHeight: '18px',
    height: '100%',
    whiteSpace: 'pre-line',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    alignItems: 'center',
}));

const StyDate = styled('span', {
    name: 'StyDate',
})(({ theme }) => ({
    color: theme.palette.mediumGrey,
    fontSize: '12px',
    fontWeight: 300,
    letterSpacing: '0.42855px',
}));

type Props = {
    notification: Notification;
};

const UnreadNotification = ({ notification }: Props): JSX.Element => {
    const location = useLocation();
    const theme = useTheme();
    const { t } = useTranslation();

    const { mutate: markSingleRead } = useMarkSingleNotificationRead();

    const searchPart = notification.redirectTo.search
        ? { search: encodeURI(notification.redirectTo.search) }
        : {};

    const hashPart = notification.redirectTo.hash
        ? { hash: encodeURI(notification.redirectTo.hash) }
        : {};

    const onNotificationClick = () => {
        markSingleRead(notification.id);
    };

    return (
        <Flex align="center" sx={{ gap: 2 }}>
            <AvatarComponent name={notification.actorName} />
            <Flex align="start" justify="space-between">
                <Link
                    to={{
                        pathname: notification.redirectTo.pathname
                            .split('/')
                            .map(encodeURIComponent)
                            .join('/'),
                        ...searchPart,
                        ...hashPart,
                    }}
                    state={{ parentLocation: location }}
                    onClick={onNotificationClick}
                    style={{ textDecoration: 'inherit', color: 'inherit' }}
                >
                    <StyContainer>
                        <Trans
                            i18nKey={`notification.reasons.${notification.key}`}
                            tOptions={{
                                actorName: notification.actorName,
                                redirectTo: notification.redirectTo,
                                entityLabel: notification.entityLabel,
                            }}
                            components={{
                                bold: (
                                    <strong
                                        style={{
                                            color: theme.palette.primary.main,
                                        }}
                                    />
                                ),
                            }}
                        />
                    </StyContainer>
                    <StyDate>
                        {getCreationDate(notification.createdAt, t)}
                    </StyDate>
                </Link>
            </Flex>
        </Flex>
    );
};

export default UnreadNotification;
